import { Fragment, useState } from 'react';

import { List, ListItemButton, ListSubheader, styled } from '@mui/material';

import { Box, ContextMenu } from '@app-velocity/ui';

import { useWorkspaceSwitcherData } from '@app-velocity/modules/teams';

import { DropdownIcon } from '@app-velocity/icons';

import { SingleTeam } from './components/SingleTeam';

const StyledOrganizationName = styled(ListSubheader)(({ theme }) => ({
  background: 'transparent',
  padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
  lineHeight: 1.5,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: `${theme.spacing(0)} ${theme.spacing(1)}`,
  borderRadius: theme.spacing(1),
  justifyContent: 'space-between',
}));

const StyledTeamSwitcher = styled(Box)(({ theme }) => ({
  border: '1px solid var(--stroke-stroke-primary-color)',
  borderRadius: theme.spacing(2),
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  svg: {
    stroke: 'var(--icon-icon-secondary-color)',
  },
}));

const StyledMenuItem = styled(ContextMenu.Item)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
  minHeight: theme.spacing(10),
  borderRadius: theme.spacing(2),
}));

const StyledContextMenu = styled(ContextMenu)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  '.MuiPaper-root': {
    borderRadius: theme.spacing(2),
  },
}));

const StyledDivider = styled(ContextMenu.Divider)({
  borderBottomWidth: 1,
  margin: 0,
});

export const WorkspaceSelector = () => {
  const { teams, activeTeam } = useWorkspaceSwitcherData();

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  if (!activeTeam) {
    return null;
  }

  const displayDivider =
    Object.keys(teams.byOrganization).length > 0 &&
    teams.noOrganization.length > 0;

  return (
    <StyledTeamSwitcher>
      <List disablePadding sx={{ height: '100%' }}>
        <StyledListItemButton
          sx={{ height: '100%' }}
          onClick={(e) => setAnchor(e.currentTarget)}
          data-testid="active-workspace-button"
        >
          <SingleTeam team={activeTeam} />
          <DropdownIcon />
        </StyledListItemButton>
      </List>

      {teams && (
        <StyledContextMenu
          anchor={anchor}
          closeMenu={() => setAnchor(null)}
          color="light"
        >
          {Object.keys(teams.byOrganization).map((organizationId) => (
            <Fragment key={organizationId}>
              <StyledOrganizationName>
                {teams.byOrganization[organizationId].organizationName}
              </StyledOrganizationName>
              {teams.byOrganization[organizationId].teams.map((team) => (
                <StyledMenuItem
                  key={team.teamId}
                  href={`/${team.teamId!}/${team.workspaceId!}`}
                >
                  <SingleTeam team={team} />
                </StyledMenuItem>
              ))}
            </Fragment>
          ))}
          {displayDivider ? <StyledDivider /> : null}
          {teams.noOrganization.map((team) => (
            <StyledMenuItem
              key={team.teamId}
              href={`/${team.teamId!}/${team.workspaceId!}`}
            >
              <SingleTeam team={team} key={team.teamId} />
            </StyledMenuItem>
          ))}
        </StyledContextMenu>
      )}
    </StyledTeamSwitcher>
  );
};
